import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Wifi,
  WifiOff,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Disclosure } from "@/components/candidate/Disclosure";
import {
  runConnectionCheck,
  type ConnectionCheckResult,
  type ConnectionPhase,
  type ConnectionVerdict,
} from "@/lib/connectionCheck";
import { PYODIDE_STDLIB_ZIP } from "@/lib/pyodide";
import { isServerLanguage } from "@/lib/runtimes";

/** Same-origin round-trip target: a no-DB handler with no rate limit. */
const PROBE_URL = "/api/health";

type State =
  | { kind: "idle" }
  | { kind: "checking"; phase: ConnectionPhase }
  | { kind: "done"; result: ConnectionCheckResult }
  | { kind: "failed" };

/**
 * CP-17 — voluntary "Test your connection" disclosure on the Welcome step,
 * the sibling of CameraPreflight. Measures round-trip time, lost requests and
 * download throughput against our own origin (see lib/connectionCheck.ts) and
 * shows an ADVISORY verdict plus the tips Emilia asked for. Never a gate: the
 * Start button does not know this component exists, nothing is emitted to the
 * server, nothing is persisted — a re-open after collapsing shows the last
 * result because the panel unmounts, not the component (same as camera).
 */
export function ConnectionPreflight({
  codeLanguages,
}: {
  /** FEAT-6: the languages of this test's coding questions (empty = none).
   *  Only Python pays the ~14 MB Pyodide download, so only Python turns the
   *  stricter bandwidth bars on; a JavaScript / SQL-only test downloads
   *  about 1 MB and is measured like an MCQ test. */
  codeLanguages: string[];
}) {
  const heavy = codeLanguages.includes("python");
  // FEAT-12: only the BROWSER languages download a runtime; one that runs on
  // our servers downloads nothing, so it never turns a bandwidth line on —
  // it makes every Run a round trip instead (`remote`, the latency line).
  const light =
    codeLanguages.some((l) => !isServerLanguage(l)) && !heavy;
  const remote = codeLanguages.some((l) => isServerLanguage(l));
  const [state, setState] = useState<State>({ kind: "idle" });
  // Created lazily in run() — never in an effect, where StrictMode's simulated
  // cleanup would abort it before the candidate had clicked anything. Aborted
  // on unmount (leaving Welcome mid-download must not let a 2 MB fetch race
  // the Pyodide preload) and before a re-run.
  const ctrlRef = useRef<AbortController | null>(null);
  useEffect(() => () => ctrlRef.current?.abort(), []);

  const run = async (): Promise<void> => {
    ctrlRef.current?.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    // Every post-await state write is gated on still being the live run, so a
    // superseded or unmounted run can never paint over a newer one.
    const isCurrent = (): boolean => ctrlRef.current === ctrl;
    setState({ kind: "checking", phase: "latency" });
    try {
      const result = await runConnectionCheck({
        probeUrl: PROBE_URL,
        downloadUrl: PYODIDE_STDLIB_ZIP,
        needsBandwidth: heavy,
        signal: ctrl.signal,
        onPhase: (phase) => {
          if (isCurrent()) setState({ kind: "checking", phase });
        },
      });
      if (isCurrent()) setState({ kind: "done", result });
    } catch (err) {
      // Our own cancellation — unmount or re-run — is not a failure.
      if ((err as { name?: string } | null)?.name === "AbortError") return;
      if (isCurrent()) setState({ kind: "failed" });
    }
  };

  return (
    <Disclosure
      label="Test your connection (optional)"
      icon={Wifi}
      panelClassName="space-y-3"
    >
      {state.kind === "idle" && (
        <>
          <p className="text-muted-foreground">
            Takes about 10 seconds. We measure the response time and
            download speed between your device and our server &mdash;
            nothing about you is recorded.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void run()}
            className="cursor-pointer"
          >
            Check connection
          </Button>
        </>
      )}
      {state.kind === "checking" && (
        <p
          className="flex items-center gap-2 text-muted-foreground"
          role="status"
        >
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          {state.phase === "latency"
            ? "Measuring response time…"
            : "Measuring download speed…"}
        </p>
      )}
      {state.kind === "done" && (
        <>
          <Verdict result={state.result} heavy={heavy} light={light} remote={remote} />
          <Stats result={state.result} />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void run()}
            className="cursor-pointer"
          >
            Check again
          </Button>
        </>
      )}
      {state.kind === "failed" && (
        <>
          <p className="text-muted-foreground" role="status">
            We couldn&rsquo;t run the check. You can still start the test.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void run()}
            className="cursor-pointer"
          >
            Try again
          </Button>
        </>
      )}
      <Tips />
    </Disclosure>
  );
}

const HEADLINE: Record<ConnectionVerdict, string> = {
  good: "Your connection looks good.",
  fair: "Your connection may be slow or unstable.",
  poor: "Your connection looks weak.",
  offline: "We couldn’t reach our server.",
};

/** Verdict banner. Icon + words carry the meaning; colour only echoes it.
 *  No `warning` token exists in the theme — amber is the house caution
 *  colour (see PasteRecipientsDialog). */
function Verdict({
  result,
  heavy,
  light,
  remote = false,
}: {
  result: ConnectionCheckResult;
  /** A Python coding question is in the test (the ~14 MB download). */
  heavy: boolean;
  /** Coding questions, but JavaScript / SQL only (about 1 MB). */
  light: boolean;
  /** FEAT-12: the test has code that runs on our servers. */
  remote?: boolean;
}) {
  const { verdict, concerns, probes } = result;
  const tone =
    verdict === "good" ? "success" : verdict === "fair" ? "amber" : "danger";
  const Icon =
    verdict === "good"
      ? CheckCircle2
      : verdict === "offline"
        ? WifiOff
        : AlertTriangle;
  const box = {
    success: "border-success/40 bg-success/5",
    amber: "border-warning/40 bg-warning/10",
    danger: "border-destructive/40 bg-destructive/5",
  }[tone];
  const icon = {
    success: "text-success",
    amber: "text-warning-foreground dark:text-amber-400",
    danger: "text-destructive",
  }[tone];

  const details: string[] = [];
  if (verdict === "offline") {
    details.push("You may be offline. Reconnect, then check again.");
  } else {
    if (concerns.includes("drops")) {
      details.push(
        "Some requests to our server were lost — your connection may be unstable.",
      );
    }
    if (concerns.includes("latency")) {
      // All probes answered, just slowly: usually distance to Frankfurt, not
      // a bad link. Say so instead of blaming the candidate's connection.
      details.push(
        probes.ok === probes.total
          ? "Replies from our server take a while — you may be far from it. The test will still work; answers may take a moment to save."
          : "Replies from our server are slow.",
      );
    }
    if (remote && (concerns.includes("latency") || concerns.includes("drops"))) {
      details.push(
        "Some code in this test runs on our servers each time you press Run tests — on this connection each run will take a little longer.",
      );
    }
    if (concerns.includes("bandwidth")) {
      details.push("Your download speed is low.");
      if (heavy) {
        details.push(
          "This test includes Python coding questions, which download a larger Python environment (about 14 MB). On a slow connection the first coding question may take longer to open.",
        );
      } else if (light) {
        details.push(
          "This test includes coding questions in JavaScript or SQL, which download a small runtime (about 1 MB) — a slow connection only delays the first coding question briefly.",
        );
      }
    }
    if (verdict !== "good") {
      details.push(
        "You can still take the test. Try the tips below, then check again.",
      );
    }
  }

  return (
    <div
      role="status"
      className={`flex items-start gap-2 rounded-md border px-3 py-2 ${box}`}
    >
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${icon}`} aria-hidden="true" />
      <div className="space-y-1">
        <p className="font-medium text-foreground">{HEADLINE[verdict]}</p>
        {details.map((line) => (
          <p key={line} className="text-muted-foreground">
            {line}
          </p>
        ))}
      </div>
    </div>
  );
}

/** Three rows, not three columns: the card is narrow on a phone and
 *  "2 of 5 dropped" must never wrap inside a tile.
 *
 *  Exported for `connectionPreflightStats.test.ts` — what it pins is the
 *  accessible text of these rows, which is not what the markup looks like. */
export function Stats({ result }: { result: ConnectionCheckResult }) {
  const { latency, probes, download } = result;
  const stability =
    probes.total === 0
      ? "—"
      : probes.ok === probes.total
        ? `${probes.ok}/${probes.total} replies`
        : `${probes.total - probes.ok} of ${probes.total} dropped`;
  return (
    <dl className="divide-y divide-border rounded-md border border-border bg-card text-sm">
      <StatRow
        label="Response time"
        value={latency ? `~${Math.round(latency.medianMs)} ms` : "—"}
        note={
          latency && latency.jitterMs >= 1
            ? `±${Math.round(latency.jitterMs)} ms`
            : undefined
        }
      />
      <StatRow label="Stability" value={stability} />
      <StatRow
        label="Download speed"
        value={formatMbps(download)}
        note={download && !download.complete ? "partial" : undefined}
      />
    </dl>
  );
}

function StatRow({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-1.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono tabular-nums text-foreground">
        {value}
        {/* A real space, not only the margin. On screen `ml-1.5` alone reads
            fine — measured 6 px between "~150 ms" and "±30 ms" — but with no
            whitespace TEXT NODE between them the accessible name, and any
            copy of this row, is "~150 ms±30 ms" (2026-09-21 design review,
            which read it off the text and called it a visual bug; it is an
            assistive-technology one). The margin shrinks to match so the
            rendered gap is unchanged. */}
        {note && (
          <>
            {" "}
            <span className="ml-0.5 text-xs text-muted-foreground">{note}</span>
          </>
        )}
      </dd>
    </div>
  );
}

function formatMbps(download: ConnectionCheckResult["download"]): string {
  if (!download) return "Couldn’t measure";
  const { mbps } = download;
  if (mbps < 1) return "< 1 Mbps";
  if (mbps < 10) return `~${mbps.toFixed(1)} Mbps`;
  return `~${Math.round(mbps)} Mbps`;
}

function Tips() {
  return (
    <div className="space-y-1.5 border-t border-border pt-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        For a stable connection
      </p>
      <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
        <li>
          Close other browser tabs and apps that use the internet &mdash;
          video calls, streaming, large downloads.
        </li>
        <li>Turn off any VPN for the duration of the test.</li>
        <li>
          Use a wired connection if you can, or sit close to your Wi-Fi
          router.
        </li>
        <li>Pause cloud backups and system updates until you&rsquo;re done.</li>
      </ul>
    </div>
  );
}
